// Configuration constants
const DOCUMENT_CONFIG_KEY = "finloc_document_config";
const COMPANY_DETAILS_KEY = "finloc_company_details";
const COMPANY_TILES_KEY = "finloc_company_tiles";
const PRIMARY_COLOR_KEY = "appPrimaryColor";
const USER_TYPE_KEY = "finloc_user_type";
const MAX_STORAGE_SIZE = 5 * 1024 * 1024; // 5MB limit for safety
const STALE_THRESHOLD = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

// Types for document configuration
interface DocumentConfig {
  id: number;
  default_document_name: string;
  alias_document_english: string;
  alias_document_french: string;
  parser_function: string;
  status: string;
}

interface DocumentConfigData {
  documents: DocumentConfig[];
  timestamp: number;
  version: string;
}

// Types for company details
interface CompanyDetails {
  id: number;
  company_name: string;
  status: string;
  default_new_creation_field_name: string;
  alias_new_creation_english: string;
  alias_new_creation_french: string;
  allowed_to_creation: boolean;
  primary_field: string;
  is_vin_required: boolean;
  is_debtor_required: boolean;
  is_reg_document_required: boolean;
  created_ts: string;
  updated_ts: string;
  created_by: number | null;
  updated_by: number | null;
  view_New_Opportunity_Upload: boolean;
  view_col_regDocument_upload: boolean;
  view_col_VIN_Number: boolean;
  view_dashboard_registration_document: boolean;
  view_dashboard_VIN_ID: boolean;
  view_dashboard_ppsaonvin_document: boolean;
  view_ppsaonowner: boolean;
  view_ppsaonvin: boolean;
  view_registration: boolean;
}

interface CompanyDetailsData {
  details: CompanyDetails;
  timestamp: number;
  version: string;
}

// Types for company tiles
interface CompanyTile {
  id: number;
  default_tile_name: string;
  alias_tile_english: string;
  alias_tile_french: string;
  allowed_to_in_bound: boolean;
  allowed_to_out_bound: boolean;
  status: string;
  created_ts: string;
  updated_ts: string;
  company: number;
  created_by: number | null;
  updated_by: number | null;
  alias_new_creation_english?: string;
  alias_new_creation_french?: string;
  default_new_creation_field_name?: string;
  to_tiles?: Array<{
    id?: number;
    from_tile: number;
    to_tile: number;
    from_tile_name: string;
    to_tile_name: string;
    comments: "OPTIONAL" | "MANDATORY";
  }>;
  from_tiles?: Array<{
    id?: number;
    from_tile: number;
    to_tile: number;
    from_tile_name: string;
    to_tile_name: string;
    comments: "OPTIONAL" | "MANDATORY";
  }>;
}

interface CompanyTilesData {
  companyTiles: CompanyTile[];
  formConfig: any;
  timestamp: number;
  version: string;
}

// Basic localStorage operations
const localStorageOps = {
  setItem: (key: string, value: string): void => {
    if (typeof window !== "undefined" && window.localStorage) {
      localStorage.setItem(key, value);
    }
  },

  getItem: (key: string): string | null => {
    if (typeof window !== "undefined" && window.localStorage) {
      return localStorage.getItem(key);
    }
    return null;
  },

  removeItem: (key: string): void => {
    if (typeof window !== "undefined" && window.localStorage) {
      localStorage.removeItem(key);
    }
  },

  isAvailable: (): boolean => {
    try {
      if (typeof window === "undefined" || !window.localStorage) {
        return false;
      }

      const testKey = "__localStorage_test__";
      const testValue = "test";
      localStorage.setItem(testKey, testValue);
      const retrieved = localStorage.getItem(testKey);
      localStorage.removeItem(testKey);
      return retrieved === testValue;
    } catch (error) {
      return false;
    }
  },
};

// Check if data size is within limits
const checkStorageSize = (data: string): boolean => {
  const sizeInBytes = new Blob([data]).size;
  return sizeInBytes <= MAX_STORAGE_SIZE;
};

// Check if data is stale
const isDataStale = (timestamp: number): boolean => {
  return Date.now() - timestamp > STALE_THRESHOLD;
};

// Document configuration localStorage utilities
const documentConfigStorageUtils = {
  // Save document configuration to localStorage
  saveDocumentConfig: (data: DocumentConfig[]): boolean => {
    try {
      if (!localStorageOps.isAvailable()) {
        console.warn("localStorage is not available");
        return false;
      }

      const configData: DocumentConfigData = {
        documents: data,
        timestamp: Date.now(),
        version: "1.0",
      };

      const jsonData = JSON.stringify(configData);

      if (!checkStorageSize(jsonData)) {
        console.warn("Document config data too large for localStorage");
        // Try to store essential data only
        const essentialData: DocumentConfigData = {
          documents: data.map((doc) => ({
            id: doc.id,
            default_document_name: doc.default_document_name,
            alias_document_english: doc.alias_document_english,
            alias_document_french: doc.alias_document_french,
            parser_function: doc.parser_function,
            status: doc.status,
          })),
          timestamp: Date.now(),
          version: "1.0",
        };

        const essentialJson = JSON.stringify(essentialData);
        if (checkStorageSize(essentialJson)) {
          localStorageOps.setItem(DOCUMENT_CONFIG_KEY, essentialJson);
          return true;
        } else {
          console.error(
            "Even essential document config data is too large for localStorage",
          );
          return false;
        }
      }

      localStorageOps.setItem(DOCUMENT_CONFIG_KEY, jsonData);
      return true;
    } catch (error) {
      console.error("Failed to save document config to localStorage:", error);
      return false;
    }
  },

  // Load document configuration from localStorage
  loadDocumentConfig: (): DocumentConfig[] | null => {
    try {
      if (!localStorageOps.isAvailable()) {
        return null;
      }

      const storageData = localStorageOps.getItem(DOCUMENT_CONFIG_KEY);

      if (!storageData) {
        return null;
      }

      const parsedData: DocumentConfigData = JSON.parse(storageData);

      // Check if data is stale (older than 7 days)
      if (isDataStale(parsedData.timestamp)) {
        documentConfigStorageUtils.clearDocumentConfig();
        return null;
      }

      return parsedData.documents || null;
    } catch (error) {
      console.error("Failed to load document config from localStorage:", error);
      // Clear corrupted localStorage data
      documentConfigStorageUtils.clearDocumentConfig();
      return null;
    }
  },

  // Clear document configuration localStorage
  clearDocumentConfig: (): void => {
    try {
      localStorageOps.removeItem(DOCUMENT_CONFIG_KEY);
    } catch (error) {
      console.error("Failed to clear document config localStorage:", error);
    }
  },

  // Check if localStorage is available and working
  isStorageAvailable: (): boolean => {
    return localStorageOps.isAvailable();
  },

  // Get storage usage info
  getStorageInfo: () => {
    try {
      if (!localStorageOps.isAvailable()) {
        return { available: false };
      }

      const data = localStorageOps.getItem(DOCUMENT_CONFIG_KEY);
      const size = data ? new Blob([data]).size : 0;

      return {
        available: true,
        hasData: !!data,
        sizeBytes: size,
        sizeMB: (size / 1024 / 1024).toFixed(2),
        maxSizeMB: (MAX_STORAGE_SIZE / 1024 / 1024).toFixed(2),
      };
    } catch (error: any) {
      return { available: false, error: error.message };
    }
  },
};

// Company Details Storage Utilities
const companyDetailsStorageUtils = {
  // Save company details to localStorage with metadata
  saveCompanyDetails: (details: CompanyDetails): boolean => {
    try {
      if (!localStorageOps.isAvailable()) {
        console.warn(
          "localStorage not available, skipping company details save",
        );
        return false;
      }

      const dataWithMetadata: CompanyDetailsData = {
        details,
        timestamp: Date.now(),
        version: "1.0",
      };

      const serialized = JSON.stringify(dataWithMetadata);

      // Check size before saving
      if (serialized.length > MAX_STORAGE_SIZE) {
        console.error("Company details too large for localStorage");
        return false;
      }

      localStorageOps.setItem(COMPANY_DETAILS_KEY, serialized);
      return true;
    } catch (error) {
      console.error("Failed to save company details to localStorage:", error);
      return false;
    }
  },

  // Load company details from localStorage
  loadCompanyDetails: (): CompanyDetails | null => {
    try {
      if (!localStorageOps.isAvailable()) {
        return null;
      }

      const stored = localStorageOps.getItem(COMPANY_DETAILS_KEY);
      if (!stored) {
        return null;
      }

      const parsed: CompanyDetailsData = JSON.parse(stored);

      // Check if data is stale
      if (isDataStale(parsed.timestamp)) {
        console.warn("Company details data is stale, clearing it");
        companyDetailsStorageUtils.clearCompanyDetails();
        return null;
      }

      return parsed.details;
    } catch (error) {
      console.error("Failed to load company details from localStorage:", error);
      companyDetailsStorageUtils.clearCompanyDetails();
      return null;
    }
  },

  // Clear company details from localStorage
  clearCompanyDetails: (): void => {
    try {
      localStorageOps.removeItem(COMPANY_DETAILS_KEY);
    } catch (error) {
      console.error(
        "Failed to clear company details from localStorage:",
        error,
      );
    }
  },

  // Check if localStorage is available and functioning
  isAvailable: (): boolean => {
    return localStorageOps.isAvailable();
  },

  // Get storage usage info
  getStorageInfo: () => {
    try {
      if (!localStorageOps.isAvailable()) {
        return { available: false };
      }

      const data = localStorageOps.getItem(COMPANY_DETAILS_KEY);
      const size = data ? new Blob([data]).size : 0;

      return {
        available: true,
        hasData: !!data,
        sizeBytes: size,
        sizeMB: (size / 1024 / 1024).toFixed(2),
        maxSizeMB: (MAX_STORAGE_SIZE / 1024 / 1024).toFixed(2),
      };
    } catch (error: any) {
      return { available: false, error: error.message };
    }
  },
};

// Company Tiles Storage Utilities
const companyTilesStorageUtils = {
  // Save company tiles configuration to localStorage
  saveCompanyTiles: (
    companyTiles: CompanyTile[],
    formConfig: any,
    version: string = "1.0.0",
  ): boolean => {
    if (!localStorageOps.isAvailable()) {
      console.warn("localStorage is not available for company tiles");
      return false;
    }

    try {
      const data: CompanyTilesData = {
        companyTiles,
        formConfig,
        timestamp: Date.now(),
        version,
      };

      const serialized = JSON.stringify(data);

      if (!checkStorageSize(serialized)) {
        console.error("Company tiles data is too large to store");
        return false;
      }

      localStorageOps.setItem(COMPANY_TILES_KEY, serialized);
      return true;
    } catch (error) {
      console.error("Failed to save company tiles to localStorage:", error);
      return false;
    }
  },

  // Load company tiles configuration from localStorage
  loadCompanyTiles: (): {
    companyTiles: CompanyTile[];
    formConfig: any;
  } | null => {
    if (!localStorageOps.isAvailable()) {
      console.warn("localStorage is not available for company tiles");
      return null;
    }

    try {
      const stored = localStorageOps.getItem(COMPANY_TILES_KEY);
      if (!stored) {
        console.info("No company tiles found in localStorage");
        return null;
      }

      const data: CompanyTilesData = JSON.parse(stored);

      // Check if data is stale
      if (isDataStale(data.timestamp)) {
        companyTilesStorageUtils.clearCompanyTiles();
        return null;
      }

      // Validate data structure
      if (!data.companyTiles || !Array.isArray(data.companyTiles)) {
        console.error("Invalid company tiles data structure in localStorage");
        companyTilesStorageUtils.clearCompanyTiles();
        return null;
      }
      return {
        companyTiles: data.companyTiles,
        formConfig: data.formConfig || {},
      };
    } catch (error) {
      console.error("Failed to load company tiles from localStorage:", error);
      companyTilesStorageUtils.clearCompanyTiles();
      return null;
    }
  },

  // Clear company tiles configuration from localStorage
  clearCompanyTiles: (): void => {
    if (!localStorageOps.isAvailable()) {
      console.warn("localStorage is not available for company tiles");
      return;
    }

    try {
      localStorageOps.removeItem(COMPANY_TILES_KEY);
      console.log("Company tiles cleared from localStorage");
    } catch (error) {
      console.error("Failed to clear company tiles from localStorage:", error);
    }
  },

  // Check if localStorage is available and functioning
  isAvailable: (): boolean => {
    return localStorageOps.isAvailable();
  },

  // Get storage usage info for company tiles
  getStorageInfo: () => {
    try {
      if (!localStorageOps.isAvailable()) {
        return { available: false };
      }

      const data = localStorageOps.getItem(COMPANY_TILES_KEY);
      const size = data ? new Blob([data]).size : 0;

      return {
        available: true,
        hasData: !!data,
        sizeBytes: size,
        sizeMB: (size / 1024 / 1024).toFixed(2),
        maxSizeMB: (MAX_STORAGE_SIZE / 1024 / 1024).toFixed(2),
      };
    } catch (error: any) {
      return { available: false, error: error.message };
    }
  },
};

// User Type Storage Utilities
export type UserType = "superadmin" | "admin" | "enduser" | "manager" | "unknown";

interface UserTypeData {
  userType: UserType;
  featureId: number;
  timestamp: number;
  version: string;
}

const userTypeStorageUtils = {
  // Save user type to localStorage
  saveUserType: (userType: UserType, featureId: number): boolean => {
    try {
      if (!localStorageOps.isAvailable()) {
        console.warn("localStorage not available, skipping user type save");
        return false;
      }

      const dataWithMetadata: UserTypeData = {
        userType,
        featureId,
        timestamp: Date.now(),
        version: "1.0",
      };

      const serialized = JSON.stringify(dataWithMetadata);
      localStorageOps.setItem(USER_TYPE_KEY, serialized);
      return true;
    } catch (error) {
      console.error("Failed to save user type to localStorage:", error);
      return false;
    }
  },

  // Load user type from localStorage
  loadUserType: (): { userType: UserType; featureId: number } | null => {
    try {
      if (!localStorageOps.isAvailable()) {
        return null;
      }

      const stored = localStorageOps.getItem(USER_TYPE_KEY);
      if (!stored) {
        return null;
      }

      const parsed: UserTypeData = JSON.parse(stored);

      // Check if data is stale (older than 7 days)
      if (isDataStale(parsed.timestamp)) {
        console.warn("User type data is stale, clearing it");
        userTypeStorageUtils.clearUserType();
        return null;
      }

      return {
        userType: parsed.userType,
        featureId: parsed.featureId,
      };
    } catch (error) {
      console.error("Failed to load user type from localStorage:", error);
      userTypeStorageUtils.clearUserType();
      return null;
    }
  },

  // Clear user type from localStorage
  clearUserType: (): void => {
    try {
      localStorageOps.removeItem(USER_TYPE_KEY);
    } catch (error) {
      console.error("Failed to clear user type from localStorage:", error);
    }
  },

  // Check if localStorage is available
  isAvailable: (): boolean => {
    return localStorageOps.isAvailable();
  },

  // Get storage info for user type
  getStorageInfo: () => {
    try {
      if (!localStorageOps.isAvailable()) {
        return { available: false };
      }

      const data = localStorageOps.getItem(USER_TYPE_KEY);
      const size = data ? new Blob([data]).size : 0;

      return {
        available: true,
        hasData: !!data,
        sizeBytes: size,
      };
    } catch (error: any) {
      return { available: false, error: error.message };
    }
  },
};

// Individual function exports for easier importing
export const saveDocumentConfig = documentConfigStorageUtils.saveDocumentConfig;
export const loadDocumentConfig = documentConfigStorageUtils.loadDocumentConfig;
export const clearDocumentConfig =
  documentConfigStorageUtils.clearDocumentConfig;

export const saveCompanyDetails = companyDetailsStorageUtils.saveCompanyDetails;
export const loadCompanyDetails = companyDetailsStorageUtils.loadCompanyDetails;
export const clearCompanyDetails =
  companyDetailsStorageUtils.clearCompanyDetails;

export const saveCompanyTiles = companyTilesStorageUtils.saveCompanyTiles;
export const loadCompanyTiles = companyTilesStorageUtils.loadCompanyTiles;
export const clearCompanyTiles = companyTilesStorageUtils.clearCompanyTiles;

export const saveUserType = userTypeStorageUtils.saveUserType;
export const loadUserType = userTypeStorageUtils.loadUserType;
export const clearUserType = userTypeStorageUtils.clearUserType;

// Clear all stored finloc data from localStorage
export const clearAllStoredData = (): void => {
  clearDocumentConfig();
  clearCompanyDetails();
  clearCompanyTiles();
  clearUserType();
};

// Get information about stored data
export const getStorageInfo = (): {
  isAvailable: boolean;
  documentConfig: { exists: boolean; size?: number; timestamp?: number };
  companyDetails: { exists: boolean; size?: number; timestamp?: number };
  companyTiles: { exists: boolean; size?: number; timestamp?: number };
} => {
  const info = {
    isAvailable: localStorageOps.isAvailable(),
    documentConfig: { exists: false } as any,
    companyDetails: { exists: false } as any,
    companyTiles: { exists: false } as any,
  };

  if (info.isAvailable) {
    // Check document config
    const docStored = localStorageOps.getItem(DOCUMENT_CONFIG_KEY);
    if (docStored) {
      try {
        const docData: DocumentConfigData = JSON.parse(docStored);
        info.documentConfig = {
          exists: true,
          size: new Blob([docStored]).size,
          timestamp: docData.timestamp,
        };
      } catch (error) {
        console.error(
          "Error parsing document config from localStorage:",
          error,
        );
      }
    }

    // Check company details
    const companyStored = localStorageOps.getItem(COMPANY_DETAILS_KEY);
    if (companyStored) {
      try {
        const companyData: CompanyDetailsData = JSON.parse(companyStored);
        info.companyDetails = {
          exists: true,
          size: new Blob([companyStored]).size,
          timestamp: companyData.timestamp,
        };
      } catch (error) {
        console.error(
          "Error parsing company details from localStorage:",
          error,
        );
      }
    }

    // Check company tiles
    const tilesStored = localStorageOps.getItem(COMPANY_TILES_KEY);
    if (tilesStored) {
      try {
        const tilesData: CompanyTilesData = JSON.parse(tilesStored);
        info.companyTiles = {
          exists: true,
          size: new Blob([tilesStored]).size,
          timestamp: tilesData.timestamp,
        };
      } catch (error) {
        console.error("Error parsing company tiles from localStorage:", error);
      }
    }
  }

  return info;
};

export {
  documentConfigStorageUtils,
  companyDetailsStorageUtils,
  companyTilesStorageUtils,
  userTypeStorageUtils,
  localStorageOps,
  DOCUMENT_CONFIG_KEY,
  COMPANY_DETAILS_KEY,
  COMPANY_TILES_KEY,
  PRIMARY_COLOR_KEY,
  USER_TYPE_KEY,
};

export type {
  DocumentConfig,
  DocumentConfigData,
  CompanyDetails,
  CompanyDetailsData,
  CompanyTile,
  CompanyTilesData,
};

// ============================================
// Primary Color Management Functions
// ============================================

/**
 * Save primary color to localStorage
 * @param color - Hex color code (e.g., "#3b82f6")
 * @returns boolean indicating success
 */
export const savePrimaryColor = (color: string): boolean => {
  try {
    if (!/^#[0-9A-F]{6}$/i.test(color)) {
      console.error("Invalid color format. Must be a hex color (e.g., #3b82f6)");
      return false;
    }
    localStorage.setItem(PRIMARY_COLOR_KEY, color);
    // Apply to CSS variable for immediate effect
    if (typeof document !== 'undefined') {
      document.documentElement.style.setProperty('--color-primary', color);
    }
    return true;
  } catch (error) {
    console.error("Error saving primary color:", error);
    return false;
  }
};

/**
 * Get primary color from localStorage
 * @returns color string or null if not found
 */
export const getPrimaryColor = (): string | null => {
  try {
    const color = localStorage.getItem(PRIMARY_COLOR_KEY);
    return color;
  } catch (error) {
    console.error("Error retrieving primary color:", error);
    return null;
  }
};

/**
 * Remove primary color from localStorage
 * @returns boolean indicating success
 */
export const clearPrimaryColor = (): boolean => {
  try {
    localStorage.removeItem(PRIMARY_COLOR_KEY);
    // Reset CSS variable to default
    if (typeof document !== 'undefined') {
      document.documentElement.style.removeProperty('--color-primary');
    }
    return true;
  } catch (error) {
    console.error("Error clearing primary color:", error);
    return false;
  }
};

/**
 * Apply stored primary color to document
 * Call this on app initialization
 */
export const applyStoredPrimaryColor = (): void => {
  try {
    const color = getPrimaryColor();
    if (color && typeof document !== 'undefined') {
      document.documentElement.style.setProperty('--color-primary', color);
    }
  } catch (error) {
    console.error("Error applying stored primary color:", error);
  }
};

